import { Component, ErrorInfo, ReactNode } from 'react';
import { trackException } from '../services/appInsights';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export default class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    trackException(error, {
      componentStack: errorInfo.componentStack || '',
      errorBoundary: 'AppErrorBoundary',
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
    console.error('Application Error:', error, errorInfo);
  }

  handleReload = () => window.location.reload();
  handleGoHome = () => (window.location.href = '/');

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          style={{
            minHeight: '100dvh',
            background: 'var(--bg)',
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div style={{ maxWidth: 480, textAlign: 'center' }}>
            <div className="eyebrow" style={{ color: 'var(--danger)', marginBottom: 12 }}>Error</div>
            <h1 className="display tight" style={{ fontSize: 32, margin: '0 0 14px' }}>Something went wrong</h1>
            <p className="muted-2" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 24 }}>
              We apologize for the inconvenience. An unexpected error has occurred. Our team has been notified.
            </p>

            {this.state.error && import.meta.env.DEV && (
              <details style={{ marginBottom: 24, textAlign: 'left' }}>
                <summary className="muted" style={{ cursor: 'pointer', marginBottom: 6 }}>
                  Technical details (development only)
                </summary>
                <div
                  className="mono"
                  style={{
                    padding: '12px 14px',
                    background: 'var(--bg-2)',
                    border: '1px solid var(--line)',
                    borderRadius: 10,
                    fontSize: 12,
                    color: 'var(--danger)',
                    maxHeight: 240,
                    overflow: 'auto',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    {this.state.error.name}: {this.state.error.message}
                  </div>
                  {this.state.error.stack && (
                    <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0 }}>{this.state.error.stack}</pre>
                  )}
                </div>
              </details>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button type="button" onClick={this.handleReload} className="btn primary lg">
                Reload page
              </button>
              <button type="button" onClick={this.handleGoHome} className="btn lg">
                Go to home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
